import {
  HHChip,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import EditIcon from '@mui/icons-material/Edit';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  ActiveStatus,
  calculateChipColor,
} from '../../../billing-tool/utils/contract-utils';
import { formatCurrency } from '../../../billing-tool/utils/currency-helpers';
import { formatDate } from '../../../billing-tool/utils/date-helper';
import {
  SubscriptionEstimateColumnFields,
  SubscriptionEstimateColumnHeaderNames,
  SubscriptionEstimateRow,
  SUBSCRIPTION_ESTIMATES_EDIT_ACTION_TEST_ID,
} from './types';
import { NULL_PLACEHOLDER } from './utils';

const StatusCell = ({ status }: { status: ActiveStatus }): JSX.Element => (
  <HHChip
    hhVariant="variant-bypass"
    color={calculateChipColor(status)}
    size="small"
    label={status}
  />
);

const LastModifiedCell = ({
  row,
}: {
  row: SubscriptionEstimateRow;
}): JSX.Element => (
  <HHStack direction="column" spacing={0}>
    <HHTypography hhVariant="body2" sx={{ fontWeight: 700 }}>
      {row.lastModifiedDisplay}
    </HHTypography>
    <HHTypography
      hhVariant="caption"
      color={(theme): string => theme.palette.text.secondary}
    >
      {formatDate(row.updatedAt)}
    </HHTypography>
  </HHStack>
);

const SHARED_COLUMNS: GridColDef[] = [
  {
    field: SubscriptionEstimateColumnFields.GROSS,
    headerName: SubscriptionEstimateColumnHeaderNames.GROSS,
    width: 120,
    disableColumnMenu: true,
    // Raw number so the default comparator sorts numerically; no type: 'number'
    // to keep the column left-aligned per the grid's design.
    valueGetter: (params): number =>
      (params.row as SubscriptionEstimateRow).grossAmount,
    valueFormatter: (params): string =>
      formatCurrency(params.value as number, 'USD'),
  },
  {
    field: SubscriptionEstimateColumnFields.RESERVE,
    headerName: SubscriptionEstimateColumnHeaderNames.RESERVE,
    width: 120,
    disableColumnMenu: true,
    valueGetter: (params): number =>
      (params.row as SubscriptionEstimateRow).reserveAmount,
    valueFormatter: (params): string =>
      formatCurrency(params.value as number, 'USD'),
  },
  {
    field: SubscriptionEstimateColumnFields.NET,
    headerName: SubscriptionEstimateColumnHeaderNames.NET,
    width: 120,
    disableColumnMenu: true,
    valueGetter: (params): number =>
      (params.row as SubscriptionEstimateRow).netAmount,
    valueFormatter: (params): string =>
      formatCurrency(params.value as number, 'USD'),
  },
  {
    field: SubscriptionEstimateColumnFields.START_DATE,
    headerName: SubscriptionEstimateColumnHeaderNames.START_DATE,
    width: 140,
    type: 'date',
    disableColumnMenu: true,
    // Raw Date so the date comparator sorts chronologically; parse with dayjs to
    // match formatDate's parsing.
    valueGetter: (params): Date =>
      dayjs((params.row as SubscriptionEstimateRow).startDate).toDate(),
    valueFormatter: (params): string =>
      params.value ? formatDate(params.value as Date) : NULL_PLACEHOLDER,
  },
  {
    field: SubscriptionEstimateColumnFields.END_DATE,
    headerName: SubscriptionEstimateColumnHeaderNames.END_DATE,
    width: 140,
    type: 'date',
    disableColumnMenu: true,
    // Null end dates (open-ended periods) sort to the end.
    valueGetter: (params): Date | null => {
      const { endDate } = params.row as SubscriptionEstimateRow;
      return endDate ? dayjs(endDate).toDate() : null;
    },
    valueFormatter: (params): string =>
      params.value ? formatDate(params.value as Date) : NULL_PLACEHOLDER,
  },
  {
    field: SubscriptionEstimateColumnFields.STATUS,
    headerName: SubscriptionEstimateColumnHeaderNames.STATUS,
    width: 120,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (
      params: GridRenderCellParams<SubscriptionEstimateRow>,
    ): JSX.Element => <StatusCell status={params.row.status} />,
  },
  {
    field: SubscriptionEstimateColumnFields.LAST_MODIFIED,
    headerName: SubscriptionEstimateColumnHeaderNames.LAST_MODIFIED,
    width: 160,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (
      params: GridRenderCellParams<SubscriptionEstimateRow>,
    ): JSX.Element => <LastModifiedCell row={params.row} />,
  },
];

// The actions column reads the edit handler, so it is built inside the hook
// via useMemo (see below); only the read-only columns stay module-scope.
const buildActionsColumn = (
  onEdit: (row: SubscriptionEstimateRow) => void,
): GridColDef => ({
  field: SubscriptionEstimateColumnFields.ACTIONS,
  headerName: SubscriptionEstimateColumnHeaderNames.ACTIONS,
  type: 'actions',
  width: 100,
  // Past periods are historical and never editable.
  getActions: (
    params: GridRowParams<SubscriptionEstimateRow>,
  ): JSX.Element[] =>
    params.row.status === ActiveStatus.Past
      ? []
      : [
          <GridActionsCellItem
            key={`edit-${params.id}`}
            icon={<EditIcon />}
            label="Edit"
            data-testid={SUBSCRIPTION_ESTIMATES_EDIT_ACTION_TEST_ID}
            onClick={(): void => onEdit(params.row)}
          />,
        ],
});

export const useSubscriptionEstimateColumns = ({
  onEdit,
}: {
  onEdit: (row: SubscriptionEstimateRow) => void;
}): GridColDef[] =>
  useMemo(() => [...SHARED_COLUMNS, buildActionsColumn(onEdit)], [onEdit]);
